import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ElementRef,
  Optional,
  Self,
  AfterViewInit,
  HostBinding,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import * as moment from 'moment';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  selector: 'app-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrls: ['./date-range-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: DateRangeInputComponent
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateRangeInputComponent
  implements
    MatFormFieldControl<any>,
    OnInit,
    ControlValueAccessor,
    OnDestroy,
    AfterViewInit {
  static nextId = 0;

  stateChanges = new Subject<any>();
  errorState = false;
  controlType = 'date-range-input';

  get empty() {
    return this.value ? !this.value.startDate && !this.value.endDate : false;
  }

  @HostBinding('class.date-range-floating')
  get shouldLabelFloat() {
    return !this.empty || this._focused;
  }

  set focused(v) {
    this._focused = v;
    this.stateChanges.next();
  }
  get focused() {
    return this._focused;
  }
  private _focused = true;

  @HostBinding()
  set id(v) {
    this._id = v;
  }
  get id() {
    return this._id;
  }
  private _id = `date-range-input-${DateRangeInputComponent.nextId++}`;

  @HostBinding('attr.aria-describedby')
  get describedBy() {
    return this._describedBy;
  }
  set describedBy(v) {
    this._describedBy = v;
  }
  private _describedBy = '';

  @Input()
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  get placeholder(): string {
    return this._placeholder;
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): DateRange {
    return this._value;
  }
  set value(v: DateRange) {
    if (v && v.startDate) {
      v.startDate = moment(v.startDate.toDate().toISOString());
    }
    if (v && v.endDate) {
      v.endDate = moment(v.endDate.toDate().toISOString());
    }
    this._value = v;
    this.stateChanges.next();
    this.onChange(v);
  }
  private _value;

  __ranges: any = {
    Today: [moment().startOf('day'), moment().endOf('day')],
    Yesterday: [
      moment()
        .subtract(1, 'days')
        .startOf('day'),
      moment()
        .subtract(1, 'days')
        .endOf('day')
    ],
    'Last 7 Days': [
      moment()
        .subtract(6, 'days')
        .startOf('day'),
      moment().endOf('day')
    ],
    'Last 30 Days': [
      moment()
        .subtract(29, 'days')
        .startOf('day'),
      moment().endOf('day')
    ],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [
      moment()
        .subtract(1, 'month')
        .startOf('month'),
      moment()
        .subtract(1, 'month')
        .endOf('month')
    ]
  };

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange = (_: any) => {};
  onTouched = () => {};
  ngOnInit() {
    this.onTouched();
  }

  writeValue(value) {
    this.value = value;
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  ngAfterViewInit() {
    this._elementRef.nativeElement.classList.add('refresh');
    this._focused = true;
    this.stateChanges.next();
    setTimeout(() => {
      this._focused = false;
      this.stateChanges.next();
    }, 50);
  }

  onContainerClick(event: MouseEvent) {
    this.stateChanges.next();
  }
}

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}
